import { debounce } from "lodash";
import { useCallback, useContext, useRef, useState } from "react";
import {
	components,
	GroupBase,
	MenuListProps,
	OptionsOrGroups,
} from "react-select";
import AsyncSelect from "react-select/async";
import { useParams } from "react-router";
import { Constants } from "../../../../common/constants";
import { Mentionable } from "../../../../common/sesh_types";
import { trpc } from "../../dashboard/utils/trpc";
import {
	dropdownColourStyles,
	DropdownMemberLabel,
	DropdownMemberSelectedLabel,
	SingleValue,
} from "../../dashboard/forms/searchable_dropdown";
import {
	GuildMemberContext,
	useGuildMemberQuery,
} from "../../dashboard/forms/improved_components/hooks/use_guild_members";
import { useRoles } from "../../dashboard/forms/improved_components/hooks/use_roles";

type CombinedMentionsValue = {
	user_mentions: string[];
	role_mentions: Mentionable[];
};

type CombinedMentionItem = {
	label: React.ReactNode;
	selectedLabel?: React.ReactNode;
	value: string;
	color: string;
	type: "user" | "role";
	data?: Mentionable;
};

function MemberToDropdownItem(
	members_map: Record<string, Mentionable>,
	id: string
): CombinedMentionItem {
	const member = members_map[id];
	const isDisabled = member?.disabled || false;

	return {
		label: (
			<DropdownMemberLabel
				val={member}
				disabled={
					isDisabled
						? {
								text_element: "Invalid permissions",
								class: "dropdown-disabled-text",
						  }
						: undefined
				}
			/>
		),
		selectedLabel: isDisabled ? (
			<div className="opacity-50">
				<DropdownMemberSelectedLabel val={member} />
			</div>
		) : (
			<DropdownMemberSelectedLabel val={member} />
		),
		value: id,
		color: "#ffffff",
		type: "user",
	};
}

function RoleToDropdownItem(role: Mentionable): CombinedMentionItem {
	const isDisabled = role.disabled || false;
	const disabledClass = isDisabled ? "opacity-50" : "";
	
	return {
		label: (
			<div className={`flex items-center gap-2 ${disabledClass}`}>
				<div
					className="h-3 w-3 rounded-full"
					style={{ backgroundColor: role.color || "#99aab5" }}
				/>
				<span>{role.name}</span>
				<span className="text-xs text-white/50">(role)</span>
				{isDisabled && (
					<span className="text-xs text-red-400">
						- Invalid permissions
					</span>
				)}
			</div>
		),
		selectedLabel: (
			<div className={`flex items-center gap-2 ${disabledClass}`}>
				<div
					className="h-3 w-3 rounded-full"
					style={{ backgroundColor: role.color || "#99aab5" }}
				/>
				<span>{role.name}</span>
				{isDisabled && (
					<span className="text-xs text-red-400">
						- Invalid permissions
					</span>
				)}
			</div>
		),
		value: role.id,
		color: "#ffffff",
		type: "role",
		data: role,
	};
}

const MenuList = (props: MenuListProps) => {
	return (
		<components.MenuList {...props}>
			{props.children}
			{props.options.length > Constants.GUILD_MEMBERS_SEARCH_LIMIT && (
				<p className="py-2 text-center text-xs font-bold text-sesh-primary">
					More results than can be displayed. Type to filter.
				</p>
			)}
		</components.MenuList>
	);
};

const Input = (props) => <components.Input {...props} maxLength={64} />;

export function CombinedMentionsDropdown(props: {
	roles: Mentionable[] | undefined;
	initial_value: CombinedMentionsValue;
	updateFunc: (val: CombinedMentionsValue) => void;
	disabled?: boolean;
}) {
	const { server_id: guild_id } = useParams<{ server_id: string }>();
	const context = trpc.useContext();
	const roles = useRoles();

	const search_cache = useRef<Record<string, CombinedMentionItem[]>>({});

	const members = useGuildMemberQuery(
		guild_id,
		props.initial_value.user_mentions
	);

	const members_cache = useContext(GuildMemberContext);
	const members_for_guild = members_cache.getMembers(guild_id);

	const members_map: Record<string, Mentionable> = {};
	for (const member of members_for_guild) {
		members_map[member.id] = member;
	}

	// Convert current selection to dropdown items
	const currentValue: CombinedMentionItem[] = [
		...props.initial_value.user_mentions.map((id) =>
			MemberToDropdownItem(members_map, id)
		),
		...props.initial_value.role_mentions.map((role) =>
			RoleToDropdownItem(role)
		),
	];

	const fetchOptions = useCallback(
		debounce(
			(
				new_input: string,
				callback: (
					options: OptionsOrGroups<unknown, GroupBase<unknown>>
				) => void
			) => {
				if (search_cache.current[new_input]) {
					callback(search_cache.current[new_input]);
					return;
				}

				const combined_items: CombinedMentionItem[] = [];

				// Add roles first (they don't need async fetching)
				if (roles) {
					const filtered_roles = roles.filter((role) =>
						role.name
							.toLowerCase()
							.includes(new_input.toLowerCase())
					);
					combined_items.push(
						...filtered_roles.map(RoleToDropdownItem)
					);
				}

				// Then fetch members
				if (new_input.trim() === "") {
					// For empty search, return roles, currently selected members, and some initial members
					const current_member_items =
						props.initial_value.user_mentions.map((id) =>
							MemberToDropdownItem(members_map, id)
						);
					combined_items.push(...current_member_items);

					// Add some initial members from cache that aren't already selected
					const cached_members = members_for_guild.slice(0, 20); // Show first 20 cached members
					const additional_member_items = cached_members
						.filter(
							(member) =>
								!props.initial_value.user_mentions.includes(
									member.id
								)
						)
						.map((member) => {
							const isDisabled = member.disabled || false;
							return {
								label: (
									<DropdownMemberLabel
										val={member}
										disabled={
											isDisabled
												? {
														text_element: "Invalid permissions",
														class: "dropdown-disabled-text",
												  }
												: undefined
										}
									/>
								),
								selectedLabel: isDisabled ? (
									<div className="opacity-50">
										<DropdownMemberSelectedLabel val={member} />
									</div>
								) : (
									<DropdownMemberSelectedLabel val={member} />
								),
								value: member.id,
								color: "#ffffff",
								type: "user" as const,
							};
						});
					combined_items.push(...additional_member_items);

					search_cache.current[new_input] = combined_items;
					callback(combined_items);
					return;
				}

				// Check cache for previous searches
				const previous_searches = Object.keys(search_cache.current);
				for (const search of previous_searches) {
					if (search.length === 0) {
						continue;
					}

					if (
						new_input.startsWith(search) &&
						search_cache.current[search].filter(
							(item) => item.type === "user"
						).length === 0
					) {
						// No more users to find, just return roles
						search_cache.current[new_input] = combined_items;
						callback(combined_items);
						return;
					}
				}

				// Fetch members from server
				return context.members.search
					.fetch({
						guild_id: guild_id,
						query: new_input,
					})
					.then((val) => {
						members_cache.addMembers(guild_id, val);

						const member_items: CombinedMentionItem[] = val.map(
							(member) => {
								const isDisabled = member.disabled || false;
								return {
									label: (
										<DropdownMemberLabel
											val={member}
											disabled={
												isDisabled
													? {
															text_element: "Invalid permissions",
															class: "dropdown-disabled-text",
													  }
													: undefined
											}
										/>
									),
									selectedLabel: isDisabled ? (
										<div className="opacity-50">
											<DropdownMemberSelectedLabel val={member} />
										</div>
									) : (
										<DropdownMemberSelectedLabel val={member} />
									),
									value: member.id,
									color: "#ffffff",
									type: "user",
								};
							}
						);

						const final_items = [
							...combined_items,
							...member_items,
						];
						search_cache.current[new_input] = final_items;

						callback(final_items);
					});
			},
			500
		),
		[guild_id, roles, members_map]
	);

	const handleChange = (selectedItems: CombinedMentionItem[]) => {
		if (props.disabled) {
			return;
		}

		const user_mentions: string[] = [];
		const role_mentions: Mentionable[] = [];

		selectedItems.forEach((item) => {
			if (item.type === "user") {
				user_mentions.push(item.value);
			} else if (item.type === "role" && item.data) {
				role_mentions.push(item.data);
			}
		});

		props.updateFunc({
			user_mentions,
			role_mentions,
		});
	};

	return (
		<div className="flex flex-col gap-2">
			<AsyncSelect
				placeholder="Select users and roles to mention..."
				classNamePrefix="sesh-dropdown"
				isMulti
				styles={dropdownColourStyles}
				components={{ Input, SingleValue, MenuList }}
				value={currentValue}
				onChange={handleChange}
				loadOptions={(input, callback) => {
					if (props.disabled) {
						callback([]);
						return;
					}

					fetchOptions(input, callback);
				}}
				isDisabled={props.disabled}
				defaultOptions={true}
				isOptionDisabled={(data: CombinedMentionItem) => {
					if (data.type === "user") {
						const member = members_map[data.value];
						return member?.disabled || false;
					} else if (data.type === "role" && data.data) {
						return data.data.disabled || false;
					}
					return false;
				}}
				formatOptionLabel={(data: CombinedMentionItem) => {
					if (data.type === "user") {
						const member = members_map[data.value];
						const isDisabled = member?.disabled || false;
						return member ? (
							<DropdownMemberLabel
								val={member}
								disabled={
									isDisabled
										? {
												text_element: "Invalid permissions",
												class: "dropdown-disabled-text",
										  }
										: undefined
								}
							/>
						) : (
							data.value
						);
					} else {
						return data.label;
					}
				}}
			/>
		</div>
	);
}
